import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { api, buildWhatsAppLink, resolveImg } from "../lib/api";
import Seo from "../components/Seo";

// Static animation configs — extracted from render to avoid creating new objects on every paint.
const CARD_INITIAL = { opacity: 0, y: 30 };
const CARD_IN_VIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true };

export default function Prints() {
  const [prints, setPrints] = useState([]);
  useEffect(() => {
    api.get("/prints").then((r) => setPrints(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="prints-page" className="pt-[18vh] sm:pt-[22vh] pb-[10vh]">
      <Seo
        title="Prints — Impresiones fine-art numeradas"
        description="Catálogo de prints fine-art firmados y numerados por Emmanuel Upegui. Impresiones a pedido del archivo fotográfico. Consulta por WhatsApp."
        path="/prints"
        keywords="prints fotografía, impresiones fine-art, copias numeradas, fotografía edición limitada, Emmanuel Upegui prints"
      />
      <header className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw]">
        <p className="text-[11px] tracking-[0.4em] uppercase font-display text-[var(--tungsten)]">Tienda</p>
        <h1 className="font-display uppercase font-bold tracking-tighter leading-[0.85] text-[16vw] sm:text-[12vw] lg:text-[10vw] mt-3">
          Prints
        </h1>
        <p className="font-serif-accent text-xl sm:text-2xl text-white/70 mt-4 max-w-3xl font-light">
          Impresiones fine-art numeradas y firmadas a partir del archivo fotográfico.
          Cada copia se prepara a pedido. Consulta tamaños, edición y envío directamente por WhatsApp.
        </p>
      </header>

      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10 pb-24">
        {prints.map((p, i) => (
          <motion.article
            key={p.slug}
            initial={CARD_INITIAL} whileInView={CARD_IN_VIEW}
            viewport={CARD_VIEWPORT} transition={{ duration: 0.7, delay: i * 0.05 }}
            data-testid={`print-card-${p.slug}`}
            className="group"
          >
            <div className="aspect-[3/4] overflow-hidden bg-[var(--surface)]">
              <img
                src={resolveImg(p.image)}
                alt={p.title}
                loading={i < 3 ? "eager" : "lazy"}
                decoding="async"
                className="w-full h-full object-cover img-hover"
              />
            </div>
            <div className="mt-5">
              <h2 className="font-display uppercase tracking-tight text-xl sm:text-2xl">{p.title}</h2>
              <p className="font-serif-accent text-white/70 mt-2">{p.description}</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-display mt-4">
                {p.edition}
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <li key={s} className="text-[10px] tracking-[0.2em] uppercase font-display border border-white/10 px-2.5 py-1 text-white/60">
                    {s}
                  </li>
                ))}
              </ul>
              <a
                href={buildWhatsAppLink(`Hola Emmanuel, me interesa el print "${p.title}".`)}
                target="_blank" rel="noreferrer"
                data-testid={`print-whatsapp-${p.slug}`}
                className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-[var(--whatsapp)] hover:text-[var(--whatsapp)] px-5 py-3 text-[11px] tracking-[0.3em] uppercase font-display"
              >
                <ShoppingBag size={14} /> Consultar por WhatsApp
              </a>
            </div>
          </motion.article>
        ))}
      </section>

      <section className="border-t border-white/5 py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase font-display text-white/40">Sobre los prints</p>
          <p className="mt-6 font-serif-accent text-xl sm:text-2xl text-white/80 leading-[1.4]">
            Impresión sobre papel Hahnemühle Photo Rag 308g o Baryta FB 350g.
            Numerados y firmados a mano. Envío internacional disponible.
          </p>
        </div>
      </section>
    </div>
  );
}
